import React, { useState, useEffect, useRef } from "react";
import { Table, notification, Avatar, Tag, Button, Modal, Tooltip, Descriptions, Popconfirm, Input } from "antd";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import styled from "styled-components";
import API_URL from "../../config";

const axios = require("axios").default;

const PageWrap = styled.div`
  padding: 24px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #555;
`;

const DateInput = styled.input`
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: #ae094d;
  }
`;

const SearchButton = styled.button`
  background: #ae094d;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 7px 20px;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: #8e0040;
  }
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const AutoRefreshBadge = styled.span`
  font-size: 12px;
  color: #888;
  margin-left: 8px;
`;

const today = () => new Date().toISOString().slice(0, 10);

const isPast = (date, startTime) => {
  const now = new Date();
  const [h, m] = startTime.split(":").map(Number);
  const apptDate = new Date(date);
  apptDate.setHours(h, m, 0, 0);
  return apptDate < now;
};

const AttendanceBadge = ({ attended }) => {
  if (attended) {
    return <Tag color="success">Attended</Tag>;
  }
  return <Tag color="error">Absent</Tag>;
};

const MeetingTracking = () => {
  const [date, setDate] = useState(today());
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [evaluationRecord, setEvaluationRecord] = useState(null);
  const [cancelingId, setCancelingId] = useState(null);
  const [commentRecord, setCommentRecord] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const dateRef = useRef(date);

  const hasEvaluation = (record) =>
    record.listening !== null ||
    record.speaking !== null ||
    record.grammer !== null ||
    record.learned_words !== null ||
    record.unlearned_words !== null ||
    record.development_description !== null;

  useEffect(() => {
    dateRef.current = date;
  }, [date]);

  const fetchData = async (d, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API_URL}report/meeting-tracking`, {
        params: { date: d },
        withCredentials: true,
      });
      setList(res.data.list || []);
      setLastRefresh(new Date());
    } catch (err) {
      notification.error({ message: "Failed to load data." });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(today());

    const interval = setInterval(() => {
      fetchData(dateRef.current, true);
    }, 60000);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const handleSearch = () => {
    if (!date) {
      notification.warning({ message: "Please select a date." });
      return;
    }
    fetchData(date);
  };

  const handleCancelAppointment = async (scheduleTimeId) => {
    setCancelingId(scheduleTimeId);
    try {
      await axios.get(`student/plan-cancel/${scheduleTimeId}`);
      notification.success({ message: "Appointment cancelled." });
      fetchData(date, true);
    } catch (err) {
      notification.error({
        message: err?.response?.data?.message || "Failed to cancel appointment.",
      });
    } finally {
      setCancelingId(null);
    }
  };

  const openCommentEditor = (record) => {
    setCommentRecord(record);
    setCommentText(record.development_description || "");
  };

  const handleSaveComment = async () => {
    setSavingComment(true);
    try {
      await axios.put(
        `${API_URL}report/meeting-comment/${commentRecord.development_id}`,
        { description: commentText },
        { withCredentials: true }
      );
      notification.success({ message: "Comment updated." });
      setCommentRecord(null);
      fetchData(date, true);
    } catch (err) {
      notification.error({
        message: err?.response?.data?.message || "Failed to update comment.",
      });
    } finally {
      setSavingComment(false);
    }
  };

  const handleDeleteComment = async (developmentId) => {
    setDeletingCommentId(developmentId);
    try {
      await axios.delete(`${API_URL}report/meeting-comment/${developmentId}`, {
        withCredentials: true,
      });
      notification.success({ message: "Evaluation deleted." });
      fetchData(date, true);
    } catch (err) {
      notification.error({
        message: err?.response?.data?.message || "Failed to delete evaluation.",
      });
    } finally {
      setDeletingCommentId(null);
    }
  };

  const startedList = list.filter((record) => isPast(date, record.start_time));

  const columns = [
    {
      title: "",
      key: "avatar",
      width: 50,
      render: (_, record) => (
        <Avatar src={record.teacher_image_url || undefined} style={{ background: "#ae094d" }}>
          {!record.teacher_image_url
            ? (record.teacher_first_name?.[0] || "?").toUpperCase()
            : null}
        </Avatar>
      ),
    },
    {
      title: "Tutor",
      key: "teacher",
      render: (_, record) => `${record.teacher_first_name} ${record.teacher_last_name}`,
      sorter: (a, b) =>
        `${a.teacher_first_name} ${a.teacher_last_name}`.localeCompare(
          `${b.teacher_first_name} ${b.teacher_last_name}`
        ),
    },
    {
      title: "Student",
      key: "student",
      render: (_, record) =>
        record.student_first_name
          ? `${record.student_first_name} ${record.student_last_name}`
          : "-",
    },
    {
      title: "Time",
      key: "time",
      render: (_, record) => `${record.start_time} - ${record.end_time}`,
      sorter: (a, b) => a.start_time.localeCompare(b.start_time),
    },
    {
      title: "Tutor attended",
      key: "teacher_attended",
      align: "center",
      render: (_, record) => <AttendanceBadge attended={record.teacher_attended === 1} />,
    },
    {
      title: "Student attended",
      key: "student_attended",
      align: "center",
      render: (_, record) => <AttendanceBadge attended={record.student_attended === 1} />,
    },
    {
      title: "Evaluation",
      key: "evaluation",
      align: "center",
      width: 120,
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          ghost
          disabled={!hasEvaluation(record)}
          onClick={() => setEvaluationRecord(record)}
        >
          View
        </Button>
      ),
    },
    {
      title: "Teacher's comment",
      key: "development_description",
      width: 280,
      render: (_, record) => {
        const text = record.development_description;
        return (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
            {text ? (
              <Tooltip title={text} placement="topLeft">
                <span
                  style={{
                    flex: 1,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {text}
                </span>
              </Tooltip>
            ) : (
              <span style={{ flex: 1, color: "#bbb" }}>-</span>
            )}
            {/* Düzenleme yalnızca öğretmenin değerlendirme kaydı varsa mümkün */}
            {record.development_id && (
              <Tooltip title="Edit comment">
                <Button
                  size="small"
                  type="text"
                  icon={<IconPencil size={16} />}
                  onClick={() => openCommentEditor(record)}
                />
              </Tooltip>
            )}
            {record.development_id && (
              <Popconfirm
                title="Delete this evaluation (comment, scores and words)?"
                okText="Yes"
                cancelText="No"
                onConfirm={() => handleDeleteComment(record.development_id)}
              >
                <Tooltip title="Delete evaluation">
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<IconTrash size={16} />}
                    loading={deletingCommentId === record.development_id}
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </div>
        );
      },
    },
    {
      title: "",
      key: "cancel",
      align: "center",
      width: 160,
      render: (_, record) => (
        <Popconfirm
          title="Cancel this appointment?"
          okText="Yes"
          cancelText="No"
          onConfirm={() => handleCancelAppointment(record.schedule_time_id)}
        >
          <Button
            type="primary"
            danger
            size="small"
            loading={cancelingId === record.schedule_time_id}
            disabled={cancelingId !== null && cancelingId !== record.schedule_time_id}
          >
            Cancel appointment
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const getRowClassName = (record) => {
    if (record.teacher_attended !== 1) return "row-teacher-missing";
    return "";
  };

  return (
    <PageWrap>
      <h2 style={{ marginBottom: 20 }}>Meeting tracking</h2>

      <TopRow>
        <div>
          <Label>Date</Label>
          <br />
          <DateInput
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <SearchButton onClick={handleSearch} disabled={loading}>
          {loading ? "Loading..." : "List"}
        </SearchButton>
        {lastRefresh && (
          <AutoRefreshBadge>
            Last update: {lastRefresh.toLocaleTimeString("en-GB")} · Auto-refresh every 1 min
          </AutoRefreshBadge>
        )}
      </TopRow>

      <style>{`
        .row-teacher-missing td {
          background-color: #fff1f0 !important;
        }
        .row-teacher-missing:hover td {
          background-color: #ffe4e1 !important;
        }
      `}</style>

      <Table
        rowKey="schedule_time_id"
        columns={columns}
        dataSource={startedList}
        loading={loading}
        rowClassName={getRowClassName}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: "No started meetings for this date." }}
      />

      <Modal
        title="Student evaluation"
        open={!!evaluationRecord}
        onCancel={() => setEvaluationRecord(null)}
        footer={null}
        width={560}
      >
        {evaluationRecord && (
          <Descriptions
            bordered
            column={1}
            size="small"
            labelStyle={{ width: 160, fontWeight: 500 }}
          >
            <Descriptions.Item label="Student">
              {evaluationRecord.student_first_name
                ? `${evaluationRecord.student_first_name} ${evaluationRecord.student_last_name}`
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Tutor">
              {`${evaluationRecord.teacher_first_name} ${evaluationRecord.teacher_last_name}`}
            </Descriptions.Item>
            <Descriptions.Item label="Listening">
              {evaluationRecord.listening ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Speaking">
              {evaluationRecord.speaking ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Grammar">
              {evaluationRecord.grammer ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Learned words">
              {evaluationRecord.learned_words ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Unlearned words">
              {evaluationRecord.unlearned_words ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Teacher's comment">
              {evaluationRecord.development_description || "-"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title="Edit teacher's comment"
        open={!!commentRecord}
        onCancel={() => setCommentRecord(null)}
        onOk={handleSaveComment}
        okText="Save"
        confirmLoading={savingComment}
        width={560}
      >
        {commentRecord && (
          <>
            <p style={{ marginBottom: 8, color: "#555" }}>
              {`${commentRecord.teacher_first_name} ${commentRecord.teacher_last_name}`}
              {" → "}
              {`${commentRecord.student_first_name} ${commentRecord.student_last_name}`}
              {` · ${commentRecord.start_time} - ${commentRecord.end_time}`}
            </p>
            <Input.TextArea
              rows={6}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
            />
          </>
        )}
      </Modal>
    </PageWrap>
  );
};

export default MeetingTracking;
